import React from 'react';
import {
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';

// Max numbers per bulk purchase — must match the backend rail in
// POST /phones/purchase-bulk. Keeping this identical means the UI never lets
// a user pick more than the backend will accept.
export const BULK_MAX = 50;

/**
 * Step 3 — available numbers with multi-select (capped at BULK_MAX).
 * @param {Object} props - results, simulated flag, selectedNumbers Set + setter, onBack/onNext.
 */
export default function StepSelect({ results, simulated = false, selectedNumbers, setSelectedNumbers, onBack, onNext }) {
  const count = selectedNumbers.size;
  const atMax = count >= BULK_MAX;

  function toggle(phoneNumber) {
    setSelectedNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(phoneNumber)) next.delete(phoneNumber);
      else if (next.size < BULK_MAX) next.add(phoneNumber);
      return next;
    });
  }
  function selectAll() {
    setSelectedNumbers(new Set(results.slice(0, BULK_MAX).map((n) => n.phoneNumber)));
  }
  function clearAll() { setSelectedNumbers(new Set()); }

  return (
    <div className="space-y-4">
      {simulated && (
        <div
          role="status"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-medium bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
        >
          🛡️ Safe Dev Mode: Free Mock Test Numbers (No carrier charges)
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-bold text-lg">Pick one or more numbers</div>
          <div className="text-xs text-white/55">
            {results.length} available — {count} of {BULK_MAX} selected. Buy up to {BULK_MAX} at once.
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs shrink-0">
          <button
            type="button"
            onClick={selectAll}
            disabled={results.length === 0}
            className="text-civix-purple hover:underline disabled:opacity-40"
          >
            Select first {Math.min(BULK_MAX, results.length)}
          </button>
          <button
            type="button"
            onClick={clearAll}
            disabled={count === 0}
            className="text-white/50 hover:text-white/80 disabled:opacity-40"
          >
            Clear
          </button>
        </div>
      </div>
      <ul role="listbox" aria-multiselectable="true" className="space-y-2 max-h-80 overflow-y-auto pr-1">
        {results.map((n) => {
          const isSel = selectedNumbers.has(n.phoneNumber);
          const disabled = !isSel && atMax;
          return (
            <li key={n.phoneNumber}>
              <label
                aria-selected={isSel}
                className={`flex items-center justify-between gap-3 p-3 rounded-md border cursor-pointer ${
                  isSel
                    ? 'border-civix-purple/60 bg-civix-purple/10'
                    : disabled
                      ? 'border-civix-border/30 opacity-50 cursor-not-allowed'
                      : 'border-civix-border/50 hover:border-civix-purple/40'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="accent-civix-purple"
                    checked={isSel}
                    disabled={disabled}
                    onChange={() => toggle(n.phoneNumber)}
                  />
                  <span className="font-mono text-sm">{n.friendlyName || n.phoneNumber}</span>
                </div>
                <div className="text-xs text-white/55">
                  {n.locality ? `${n.locality}, ` : ''}{n.region || 'US'}
                </div>
              </label>
            </li>
          );
        })}
      </ul>
      {atMax && (
        <div className="text-xs text-amber-300">Maximum {BULK_MAX} numbers per purchase. Uncheck one to swap.</div>
      )}
      <div className="flex items-center justify-between pt-1">
        <button onClick={onBack} className="btn-ghost inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={onNext}
          disabled={count === 0}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          Continue — buy {count || 0} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
